'use client';

import { Link } from '@/lib/i18n/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { type UseFormReturn, FormProvider } from 'react-hook-form';

import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { useDebouncedCallback } from '@/hooks/useDebounce';
import { isValidEmailAddress } from '@/lib/utils/isValidEmailAddress';

import styles from '@/app/[lang]/(checkout)/store/checkout/delivery/page.module.scss';

export interface ContactInformationProps {
  email: string;
  emailError?: string;
  phoneError?: string;
  onEmailChange: (value: string) => void;
  enableEmailCheck?: boolean;
  /** When true, email is prefilled/locked (logged-in Affirm Case 2). */
  emailDisabled?: boolean;
  showHeader?: boolean;
  showDescription?: boolean;
  phoneMethods: UseFormReturn<{ phonePrefix: string; phone: string }>;
  lang: string;
  defaultCountryCode: string;
  isSubmitting: boolean;
  /** Renders without checkout section chrome; header/description should be provided by the parent. */
  embedded?: boolean;
}

export function ContactInformation({
  email,
  emailError,
  phoneError,
  onEmailChange,
  enableEmailCheck = true,
  emailDisabled = false,
  showHeader = true,
  showDescription = true,
  phoneMethods,
  lang,
  defaultCountryCode,
  isSubmitting,
  embedded = false,
}: Readonly<ContactInformationProps>) {
  const t = useTranslations('checkout');
  const [registeredEmail, setRegisteredEmail] = useState(false);

  const checkEmailRegistered = useDebouncedCallback(async (emailValue: string) => {
    if (!emailValue || !isValidEmailAddress(emailValue)) {
      setRegisteredEmail(false);
      return;
    }
    try {
      const res = await fetch('/api/auth/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue }),
      });
      if (res.ok) {
        const data = await res.json();
        setRegisteredEmail(data.data?.exists ?? false);
      }
    } catch {
      // Non-blocking — silently ignore network errors
    }
  }, 800);

  const handleEmailChange = (value: string) => {
    setRegisteredEmail(false);
    onEmailChange(value);
  };

  const Wrapper = embedded ? 'div' : 'section';
  const wrapperClassName = embedded ? styles.embedded : styles.section;

  return (
    <Wrapper className={wrapperClassName}>
      {!embedded && showHeader ? (
        <h2 className={styles.sectionTitle}>{t('delivery.contactInfo')}</h2>
      ) : null}
      {!embedded && showDescription ? (
        <p className={styles.contactDescription}>{t('delivery.contactInfoDescription')}</p>
      ) : null}

      <div className={styles.emailFieldWrapper}>
        <Input
          type="email"
          id="email"
          label={t('delivery.emailLabel')}
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          onBlur={enableEmailCheck ? () => checkEmailRegistered(email) : undefined}
          error={emailError}
          autoComplete="email"
          placeholder={t('delivery.emailPlaceholder')}
          required
          fullWidth
          disabled={isSubmitting || emailDisabled}
        />

        {enableEmailCheck && registeredEmail && (
          <div className={styles.emailRegisteredBanner}>
            <Icon name="info" size={14} />
            <p>
              {t('delivery.emailRegistered')}{' '}
              <Link href="/my-account/login?next=/store/checkout/delivery&variant=checkout">
                <strong>{t('delivery.logInNow')}</strong>
              </Link>
            </p>
          </div>
        )}
      </div>

      <FormProvider {...phoneMethods}>
        <PhoneInput
          prefixLabel={t('delivery.prefix')}
          phoneLabel={t('delivery.telephoneNumber')}
          lang={lang.split('-')[1] || lang}
          country={defaultCountryCode}
          disabled={isSubmitting}
          required
          error={phoneError}
        />
      </FormProvider>

      <p className={styles.mandatoryNote}>{t('delivery.mandatoryFields')}</p>
    </Wrapper>
  );
}
